import { motion } from 'framer-motion';
import { Activity, Loader2, RefreshCw, Inbox } from 'lucide-react';
import { useActivity } from '../hooks/useActivity';
import { ActivityCard } from '../components/activity/ActivityCard';
import { useUIStore } from '../stores/uiStore';

export function ActivityFeedPage() {
  const { selectedProjectId } = useUIStore();
  const { activities, isLoading, isLoadingMore, hasMore, error, loadMore, refresh } =
    useActivity({ projectId: selectedProjectId });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <button onClick={refresh} className="text-accent hover:underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between bg-white/80 dark:bg-[#1a1a1f]/80 backdrop-blur-xl rounded-2xl border border-border/50 p-4 shadow-sm"
      >
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-accent/10">
              <Activity className="w-6 h-6 text-accent" />
            </div>
            Activity Feed
          </h1>
          <p className="text-text-secondary mt-1">
            {selectedProjectId
              ? 'Recent updates for this project'
              : 'Recent updates across all your projects'}
          </p>
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={refresh}
          className="p-2.5 rounded-xl bg-white/80 dark:bg-[#1a1a1f]/80 backdrop-blur-xl border border-border/50 hover:border-accent/50 transition-colors shadow-sm"
          title="Refresh"
        >
          <RefreshCw className="w-5 h-5" />
        </motion.button>
      </motion.div>

      {/* Empty state */}
      {activities.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <div className="w-20 h-20 rounded-full bg-white/80 dark:bg-[#1a1a1f]/80 backdrop-blur-xl flex items-center justify-center mx-auto mb-4 shadow-sm">
            <Inbox className="w-10 h-10 text-text-muted" />
          </div>
          <h2 className="text-xl font-semibold mb-2">No activity yet</h2>
          <p className="text-text-secondary max-w-md mx-auto">
            Activity will appear here as your team shares work with you. Check back soon!
          </p>
        </motion.div>
      )}

      {/* Activity list */}
      {activities.length > 0 && (
        <div className="space-y-4">
          {activities.map((item, index) => (
            <ActivityCard key={item.id} item={item} index={index} />
          ))}

          {/* Load more */}
          {hasMore && (
            <div className="flex justify-center pt-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={loadMore}
                disabled={isLoadingMore}
                className="px-6 py-3 rounded-xl bg-white/80 dark:bg-[#1a1a1f]/80 backdrop-blur-xl border border-border/50 hover:border-accent/50 transition-colors disabled:opacity-50 shadow-sm"
              >
                {isLoadingMore ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  'Load more'
                )}
              </motion.button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Keep the old export name for router compatibility
export { ActivityFeedPage as ProjectsPage };
